import { AnimatePresence, animate, motion, useMotionValue, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";

import * as uf from "../../../../common/unfathomable";
import { CardFace, cardInfo, skillColor } from "./CardFace";
import { DragPanel } from "./DragPanel";
import { ConfirmButton } from "./Hud";
import { roundLabel } from "./Panels";
import { MythosText, PromptText, Rich } from "./RichText";
import css from "./Unfathomable.module.css";

// ================================================================ mythos card
export function MythosSlot({ view, artOk }: { view: uf.GameView; artOk: boolean }) {
  const id = view.activeMythos;
  return (
    <AnimatePresence>
      {id && (
        <motion.div key={id} className={css.mythosSlot}
          initial={{ rotateY: 180, opacity: 0, scale: 1.3, y: 60 }} animate={{ rotateY: 0, opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, x: 200, rotate: 15 }} transition={{ duration: 0.8, ease: "easeOut" }}
          style={{ perspective: 800 }}>
          <DragPanel>
            <div className={css.subtitle} style={{ textAlign: "center", textShadow: "0 0 6px #000" }}>神话卡</div>
            <CardFace kind="mythos" id={id} artOk={artOk} style={{ width: "100%" }} />
            <div className={css.mythosPanel}>
              <div className={css.mythosPanelTitle}>{uf.kMythosById[id].name}</div>
              <MythosText id={id} compact />
            </div>
          </DragPanel>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ================================================================ skill check
function Counter({ to }: { to: number }) {
  const v = useMotionValue(0);
  const rounded = useTransform(v, x => Math.round(x));
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const c = animate(v, to, { duration: 1.2, ease: "easeOut" });
    const unsub = rounded.on("change", setShown);
    return () => { c.stop(); unsub(); };
  }, [to, v, rounded]);
  return <span>{shown}</span>;
}

export function CheckPanel({ view, artOk, children }: { view: uf.GameView; artOk: boolean; children?: React.ReactNode }) {
  const c = view.check;
  const [hidden, setHidden] = useState(false);
  const lastTitle = useRef<string | null>(null);
  useEffect(() => {
    const key = c ? `${c.title}:${c.contributions.length}:${c.result}` : null;
    if (key !== lastTitle.current) setHidden(false);
    lastTitle.current = key;
  }, [c]);
  if (!c) return null;
  const names = (seat: number) => (seat < 0 ? "混沌牌" : view.players[seat]?.name ?? "?");
  const sup = new Set(c.supporting);
  const revealed = c.revealed ?? [];
  const resultCls = c.result === "pass" ? css.resultPass : c.result === "partial" ? css.resultPartial : css.resultFail;
  // Before the result is final (responses may still change it), show the running total of the
  // revealed cards: supporting skills count +, everything else −, as in the rules.
  const running = c.total ?? revealed.reduce((n, card) => {
    const d = uf.skillDef(card.def);
    return n + (d.type !== "treachery" && sup.has(d.type) ? d.value : -d.value);
  }, 0);
  const lean = running >= c.target ? "pass" : c.partial !== null && running >= c.partial ? "partial" : "fail";
  return (
    <div className={`${css.floating} ${css.checkPanelPos}`}>
    <DragPanel className={`${css.checkPanel} ${css.frame}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div className={css.subtitle} style={{ color: "var(--brass-lo)", fontSize: "1.2rem" }}>📜 {c.title}</div>
        <div>目标 <b>{c.target}</b>{c.partial !== null && <>（部分通过 {c.partial}+）</>}</div>
        <div style={{ display: "flex", gap: 3 }}>
          {c.supporting.map(t => <span key={t} className={`${css.pip} ${css[`pip_${t}`]}`}>{uf.kSkillNameZh[t]}</span>)}
        </div>
        <div style={{ flex: 1 }} />
        <button className={`${css.btn} ${css.btnSmall} ${css.btnGhost}`} onClick={() => setHidden(h => !h)}>{hidden ? "展开 ▾" : "收起 ▴"}</button>
      </div>
      {hidden ? null : revealed.length === 0 ? (
        <div className={css.checkRow} style={{ marginTop: 10 }}>
          {c.contributions.map((x, i) => (
            <div key={i} style={{ textAlign: "center" }}>
              <div style={{ display: "flex" }}>
                {Array.from({ length: Math.max(x.count, 0) }).map((_, k) => (
                  <motion.div key={k} initial={{ y: -40, opacity: 0, rotate: -10 }} animate={{ y: 0, opacity: 1, rotate: (k - x.count / 2) * 3 }}
                    transition={{ delay: k * 0.08 }} style={{ marginLeft: k ? -40 : 0 }}>
                    {x.faceUp?.[k] ? <CardFace kind="skill" id={x.faceUp[k].def} artOk={artOk} width={56} />
                      : <CardFace kind="back" id="neutral" artOk={artOk} width={56} noTip />}
                  </motion.div>
                ))}
                {x.count === 0 && <div style={{ width: 56, height: 86, border: "1px dashed var(--brass-lo)", borderRadius: 6 }} />}
              </div>
              <div style={{ fontSize: "0.75rem" }}>{names(x.seat)} ×{x.count}</div>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className={css.checkRow} style={{ marginTop: 10 }}>
            {revealed.map((card, i) => {
              const d = uf.skillDef(card.def);
              const good = d.type !== "treachery" && sup.has(d.type);
              return (
                <motion.div key={card.uid} initial={{ rotateY: 180, opacity: 0 }} animate={{ rotateY: 0, opacity: 1 }}
                  transition={{ delay: i * 0.12, duration: 0.45 }} style={{ textAlign: "center" }}>
                  <CardFace kind="skill" id={card.def} artOk={artOk} width={60} />
                  <div style={{ fontWeight: 700, color: good ? "#2f7a4b" : "#8e1f1f" }}>{good ? "+" : "−"}{d.value}</div>
                </motion.div>
              );
            })}
          </div>
          <div className={`${css.checkTotal} ${c.result ? resultCls : ""}`} data-lean={c.result ? undefined : lean}>
            {!c.result && <span className={css.checkProvisional}>暂计</span>}
            <Counter to={running} /> / {c.target}
            {c.result && (
              <motion.span initial={{ scale: 3, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.9, type: "spring" }}
                style={{ marginLeft: 16 }}>
                {c.result === "pass" ? "通过" : c.result === "partial" ? "部分通过" : "失败"}
              </motion.span>
            )}
          </div>
          {!c.result && <div className={css.checkHint}>结果尚未确定：响应牌和能力还可能改变总值</div>}
        </>
      )}
      {children && <div className={css.checkDecision}>{children}</div>}
    </DragPanel>
    </div>
  );
}

// ================================================================ die roll
export function DieRoll({ view }: { view: uf.GameView }) {
  const last = [...view.events].reverse().find(e => e.ev.type === "die");
  const [shown, setShown] = useState<{ seq: number; value: number; raw: number; reason: string } | null>(null);
  const seen = useRef<number>(last?.seq ?? 0);
  useEffect(() => {
    if (!last || last.seq <= seen.current || last.ev.type !== "die") return;
    seen.current = last.seq;
    setShown({ seq: last.seq, value: last.ev.value, raw: last.ev.raw, reason: last.ev.reason });
  }, [last]);
  // Hide timer keyed on the roll itself: new broadcasts must not cancel it.
  useEffect(() => {
    if (!shown) return;
    const t = setTimeout(() => setShown(null), 2600);
    return () => clearTimeout(t);
  }, [shown]);
  return (
    <AnimatePresence>
      {shown && (
        <motion.div key={shown.seq} className={`${css.floating} ${css.dieBox}`}
          initial={{ scale: 0.2, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.4, opacity: 0, y: -60 }} transition={{ type: "spring", stiffness: 90, damping: 11 }}>
          {/* Only the die spins; the reason below stays level and readable. */}
          <motion.svg style={{ display: "block", margin: "0 auto" }} initial={{ rotate: -540 }} animate={{ rotate: 0 }} transition={{ type: "spring", stiffness: 90, damping: 11 }} width={150} height={150} viewBox="-75 -75 150 150">
            <defs>
              <linearGradient id="ufDie" x1="0" x2="1" y1="0" y2="1"><stop offset="0%" stopColor="#f6e7bf" /><stop offset="100%" stopColor="#9c7a43" /></linearGradient>
            </defs>
            <polygon points="0,-68 48,-48 68,0 48,48 0,68 -48,48 -68,0 -48,-48" fill="url(#ufDie)" stroke="#3a2a14" strokeWidth={5} />
            <polygon points="0,-68 68,0 0,68 -68,0" fill="none" stroke="rgba(58,42,20,0.5)" strokeWidth={2} />
            <text textAnchor="middle" dy={22} fontSize={64} fontFamily="var(--deco)" fill="#2b1a08">{shown.value}</text>
          </motion.svg>
          <div className={css.dieReason}>{shown.reason}{shown.raw !== shown.value ? `（${shown.raw}→${shown.value}）` : ""}</div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ================================================================ damage card
/** Every drawn damage card flips up on the board for a few seconds (queued if several). */
export function DamageReveal({ view, artOk }: { view: uf.GameView; artOk: boolean }) {
  const seen = useRef<number>(view.events.at(-1)?.seq ?? 0);
  const [queue, setQueue] = useState<{ seq: number; card: string; space: uf.SpaceId | null }[]>([]);
  useEffect(() => {
    const fresh = view.events.filter(e => e.seq > seen.current);
    if (fresh.length === 0) return;
    seen.current = fresh[fresh.length - 1].seq;
    const add: { seq: number; card: string; space: uf.SpaceId | null }[] = [];
    for (const { seq, ev } of fresh) {
      if (ev.type === "damageDrawn") add.push({ seq, card: ev.card, space: null });
      else if (ev.type === "damage") {
        const hit = add.find(x => x.card === ev.card && !x.space);
        if (hit) hit.space = ev.space;
        else setQueue(q => q.map(x => (x.card === ev.card && !x.space ? { ...x, space: ev.space } : x)));
      }
    }
    if (add.length) setQueue(q => [...q, ...add]);
  }, [view.events]);
  const shown = queue[0];
  useEffect(() => {
    if (!shown) return;
    const t = setTimeout(() => setQueue(q => q.slice(1)), 3200);
    return () => clearTimeout(t);
  }, [shown?.seq]); // eslint-disable-line react-hooks/exhaustive-deps
  const info = shown ? cardInfo("damage", shown.card) : null;
  // No AnimatePresence: back-to-back cards would leave exited copies behind. The fade-out is
  // part of a CSS animation and the element goes away with its timer.
  if (!shown || !info) return null;
  return (
    <motion.div key={shown.seq} className={css.damageReveal}
      initial={{ rotateY: 180, opacity: 0, scale: 1.4 }} animate={{ rotateY: 0, opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }} style={{ perspective: 800 }}>
      <div className={css.damageRevealLife}>
        <div className={css.revealTitle}>⚠ 损伤</div>
        <CardFace kind="damage" id={shown.card} artOk={artOk} style={{ width: "100%" }} />
        <div className={css.revealCaption}>{info.title}{shown.space ? ` → ${uf.spaceName(shown.space)}` : ""}</div>
      </div>
    </motion.div>
  );
}

// ================================================================ turn / step banner
// Action and mythos steps get full-screen notices (Announcer); this banner covers the rest.
const kStepBannerZh: Record<string, string> = { receiveSkills: "获得技能", discard: "弃牌阶段" };

/** A short banner whenever the turn or the step changes, so bot turns are easy to follow. */
export function TurnBanner({ view }: { view: uf.GameView }) {
  const key = `${view.turnNumber}:${view.step}`;
  const [shown, setShown] = useState<{ key: string; big: string; small: string } | null>(null);
  const last = useRef(key);
  useEffect(() => {
    if (key === last.current) return;
    last.current = key;
    const label = kStepBannerZh[view.step];
    if (!label || view.gameOver) return;
    const cur = view.players[view.current];
    setShown({ key, big: label, small: `${roundLabel(view.turnNumber, view.players.length)} · ${cur?.name ?? ""}${view.you === view.current ? "（你）" : ""}` });
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!shown) return;
    const t = setTimeout(() => setShown(null), 1500);
    return () => clearTimeout(t);
  }, [shown]);
  return (
    <AnimatePresence>
      {shown && (
        <motion.div key={shown.key} className={css.turnBanner}
          initial={{ opacity: 0, scaleX: 0.3 }} animate={{ opacity: 1, scaleX: 1 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.35 }}>
          <div className={css.turnBannerBig}>{shown.big}</div>
          <div>{shown.small}</div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ================================================================ activity feed
/** The last few public log lines over the board, plus whom the table is waiting for. */
export function ActivityFeed({ view }: { view: uf.GameView }) {
  const lines = view.log.filter(l => !l.flavor && !l.text.startsWith("════")).slice(-4);
  const d = view.decision;
  return (
    <div className={css.activity}>
      <AnimatePresence initial={false}>
        {lines.map((l, i) => (
          <motion.div key={l.seq} layout initial={{ opacity: 0, x: -30 }} animate={{ opacity: i === lines.length - 1 ? 1 : 0.55 + i * 0.12, x: 0 }}
            exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} className={css.activityLine}>
            {l.to ? "🔒 " : ""}<Rich text={l.text} />
          </motion.div>
        ))}
      </AnimatePresence>
      {d && !d.mine && d.kind !== "idle" && <div className={css.activityWait}>⏳ <PromptText text={d.prompt} /></div>}
    </div>
  );
}

// ================================================================ game over
/** What happened, in words: one line per way a game can end (rules RR 18). */
const kEnding: Record<uf.GameOver["reason"], { icon: string; title: string; story: (g: uf.GameOver, view: uf.GameView) => string }> = {
  arrived: {
    icon: "⚓", title: "亚特兰蒂卡号抵达了！",
    story: (_, v) => `航程累计 ${v.distance} / 12，亚特兰蒂卡号冲出迷雾，驶入了港口。深渊没能留住她。`,
  },
  resource: {
    icon: "🌊", title: "大海吞没了亚特兰蒂卡号",
    story: g => `${g.detail}。燃料、食物、理智、灵魂任何一项耗尽，船就无法再航行下去。`,
  },
  damage: {
    icon: "💥", title: "亚特兰蒂卡号断成了两截",
    story: () => "船上同时有 6 个空间受损，船体再也撑不住风浪，从中间断裂沉没。",
  },
  deepOnes: {
    icon: "🐟", title: "深潜者占领了亚特兰蒂卡号",
    story: () => "又有深潜者要爬上船，供应区却已经空了——深渊之子已经淹没了整艘船。",
  },
};

function sideOf(cards: uf.LoyaltyCard[]): { label: string; traitor: boolean } {
  if (cards.some(c => c.type === "hybrid")) return { label: "混血叛徒", traitor: true };
  if (cards.some(c => c.type === "cultist")) return { label: "邪教徒", traitor: true };
  return { label: "人类", traitor: false };
}

/**
 * The final screen: the outcome and why, who won, everyone's loyalty cards, and a button that
 * takes the room back to the lobby (anyone may press it once the game is over).
 */
export function GameOverModal({ view, artOk, onClose, onEnd, onDownload }: {
  view: uf.GameView; artOk: boolean;
  /** Hide the screen to look at the board. */
  onClose: () => void;
  /** Back to the lobby for everyone. */
  onEnd: () => void;
  onDownload: () => void;
}) {
  const g = view.gameOver;
  if (!g) return null;
  const ending = kEnding[g.reason];
  const me = view.you !== null ? g.loyalties.find(l => l.seat === view.you) : undefined;
  const iWon = view.you !== null && g.winners.includes(view.you);
  const winners = g.winners.map(s => view.players[s]?.name).filter(Boolean);
  return (
    <motion.div className={`${css.overlay} ${css.endOverlay}`} data-win={g.humansWin ? "1" : "0"} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2 }}>
      <motion.div className={`${css.gameOver} ${css.frame}`} initial={{ y: 40, scale: 0.9, opacity: 0 }} animate={{ y: 0, scale: 1, opacity: 1 }} transition={{ delay: 0.3, type: "spring", stiffness: 120, damping: 16 }}>
        <div className={css.endIcon}>{ending.icon}</div>
        <div className={css.endKicker}>{g.humansWin ? "人类阵营胜利" : "叛徒阵营胜利"}</div>
        <h1 className={css.title} style={{ color: g.humansWin ? "#2f7a4b" : "#8e1f1f", textShadow: "none", margin: "2px 0 6px" }}>{ending.title}</h1>
        <p className={css.endStory}>{ending.story(g, view)}</p>
        {me && (
          <div className={css.endMine} data-won={iWon ? "1" : "0"}>
            {iWon ? "🏆 你赢了" : "你输了"}<span>你的身份：{sideOf(me.cards).label}</span>
          </div>
        )}
        <div className={css.endRule}>
          {g.humansWin
            ? "胜利条件：船抵达目的地时，所有没有叛徒忠诚卡的玩家获胜。"
            : `胜利条件：船沉没时，混血叛徒获胜；邪教徒只有在航程达到 12 时才获胜（本局航程 ${view.distance}）。`}
          {" "}获胜者：{winners.length ? winners.join("、") : "无"}
        </div>
        <div className={css.rule} />
        <div className={css.endPlayers}>
          {g.loyalties.map(l => {
            const side = sideOf(l.cards);
            const won = g.winners.includes(l.seat);
            return (
              <div key={l.seat} className={css.endPlayer} data-won={won ? "1" : "0"}>
                <div style={{ fontWeight: 700 }}>{view.players[l.seat].name}{l.seat === view.you ? "（你）" : ""}{won ? " 🏆" : ""}</div>
                <div className={css.endSide} data-traitor={side.traitor ? "1" : "0"}>{side.label} · {uf.kCharacters[view.players[l.seat].character].name}</div>
                <div style={{ display: "flex", gap: 4, justifyContent: "center", marginTop: 4 }}>
                  {l.cards.map((c, i) => (
                    <motion.div key={c.uid} initial={{ rotateY: 180 }} animate={{ rotateY: 0 }} transition={{ delay: 0.9 + l.seat * 0.3 + i * 0.15 }}>
                      <CardFace kind="loyalty" id={c.type} artOk={artOk} width={64} />
                    </motion.div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className={css.endButtons}>
          <ConfirmButton className={`${css.btn} ${css.btnDanger}`} label="🏁 结束本局，回到大厅"
            confirmLabel="⚠ 再点一次：所有人回到大厅" onConfirm={onEnd} />
          <button className={css.btn} onClick={onDownload}>💾 下载存档</button>
          <button className={`${css.btn} ${css.btnGhost}`} onClick={onClose}>查看棋盘</button>
        </div>
        <div className={css.endHint}>任何人都可以结束本局；之后可以在大厅重新开始。</div>
      </motion.div>
    </motion.div>
  );
}

// ================================================================ undo vote
export function UndoVote({ lobby, onVote }: { lobby: uf.LobbyView; onVote: (approve: boolean) => void }) {
  const u = lobby.undo;
  if (!u) return null;
  const mine = lobby.yourSlot !== null && lobby.yourSlot in u.votes ? u.votes[lobby.yourSlot] : undefined;
  return (
    <div className={css.overlay}>
      <motion.div className={`${css.modal} ${css.frame}`} initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
        <h3 className={css.subtitle} style={{ color: "var(--brass-lo)" }}>⏪ 回退提议</h3>
        <p><b>{u.by}</b> 提议回退到：「{u.text}」之前。{u.reshuffle ? "回退后会重新洗混面朝下的牌堆。" : ""}</p>
        <p>投票：{Object.entries(u.votes).map(([slot, v]) => `${lobby.seats[Number(slot)].name ?? "?"} ${v === null ? "…" : v ? "✔" : "✘"}`).join("　")}</p>
        {mine === null && (
          <div style={{ display: "flex", gap: 10 }}>
            <button className={css.btn} onClick={() => onVote(true)}>同意</button>
            <button className={`${css.btn} ${css.btnDanger}`} onClick={() => onVote(false)}>拒绝</button>
          </div>
        )}
        {mine !== null && <p className={css.waiting}>等待其他玩家投票…</p>}
      </motion.div>
    </div>
  );
}

// ================================================================ toast
export function Toast({ text, onClose }: { text: string | null; onClose: () => void }) {
  useEffect(() => {
    if (!text) return;
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, [text, onClose]);
  return (
    <AnimatePresence>
      {text && (
        <motion.div className={`${css.floating} ${css.toast} ${css.frame}`} initial={{ x: 60, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 60, opacity: 0 }}
          onClick={onClose}>
          ⚠ {text}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export { skillColor };
